import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useDarkMode } from '../../../hooks/use-dark-mode.hook';
import type { MetricsTickPayload } from '../../../services/realtime.types';

/** Paleta semántica del CPD (misma familia que el resto del portal). */
export const CPD_COLORS = {
  human: '#579743',
  voicemail: '#CE4532',
  unknown: '#575A5C',
  bypassed: '#0070BC',
  active: '#7239EA',
  newCalls: '#0070BC',
  hangups: '#FAA916'
};

function baseOptions(isDark: boolean): Highcharts.Options {
  const text = isDark ? '#cdcdde' : '#3f4254';
  const grid = isDark ? '#2b2b40' : '#eff2f5';
  return {
    chart: { backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, animation: false },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { itemStyle: { color: text, fontWeight: '500' }, itemHoverStyle: { color: text } },
    xAxis: { labels: { style: { color: text } }, lineColor: grid, tickColor: grid },
    yAxis: {
      title: { text: undefined },
      labels: { style: { color: text } },
      gridLineColor: grid,
      allowDecimals: false,
      min: 0
    },
    tooltip: { shared: true },
    plotOptions: { series: { animation: false } }
  };
}

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

/** Serie temporal de sesiones activas + llamadas nuevas / colgadas (ventana de 10 s) por tick. */
export function ActiveSessionsChart({
  ticks,
  height = 280
}: {
  ticks: MetricsTickPayload[];
  height?: number;
}) {
  const isDark = useDarkMode();
  const options = useMemo<Highcharts.Options>(() => {
    const base = baseOptions(isDark);
    const x = ticks.map((t) => new Date(t.ts).getTime());
    return {
      ...base,
      chart: { ...base.chart, height, type: 'area' },
      xAxis: {
        ...base.xAxis,
        type: 'datetime',
        labels: { ...(base.xAxis as any).labels, format: '{value:%H:%M:%S}' }
      },
      tooltip: { shared: true, xDateFormat: '%H:%M:%S' },
      plotOptions: {
        series: { animation: false, marker: { enabled: false }, lineWidth: 2 },
        area: { fillOpacity: 0.15 }
      },
      series: [
        {
          type: 'area',
          name: 'Sesiones activas',
          color: CPD_COLORS.active,
          data: ticks.map((t, i) => [x[i], t.activeSessions])
        },
        {
          type: 'line',
          name: 'Nuevas (10 s)',
          color: CPD_COLORS.newCalls,
          data: ticks.map((t, i) => [x[i], t.windows.last10s.newCalls])
        },
        {
          type: 'line',
          name: 'Colgadas (10 s)',
          color: CPD_COLORS.hangups,
          data: ticks.map((t, i) => [x[i], t.windows.last10s.hangups])
        }
      ]
    };
  }, [ticks, isDark, height]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

/** Barras apiladas human / voicemail / unknown por operador. */
export function ByOperatorChart({
  rows,
  height = 280
}: {
  rows: Array<{ key: string; human: number; voicemail: number; unknown: number }>;
  height?: number;
}) {
  const isDark = useDarkMode();
  const options = useMemo<Highcharts.Options>(() => {
    const base = baseOptions(isDark);
    return {
      ...base,
      chart: { ...base.chart, height, type: 'column' },
      xAxis: { ...base.xAxis, categories: rows.map((r) => r.key || '—') },
      plotOptions: { series: { animation: false }, column: { stacking: 'normal', borderWidth: 0 } },
      series: [
        { type: 'column', name: 'Humano', color: CPD_COLORS.human, data: rows.map((r) => r.human) },
        {
          type: 'column',
          name: 'Buzón',
          color: CPD_COLORS.voicemail,
          data: rows.map((r) => r.voicemail)
        },
        {
          type: 'column',
          name: 'Sin veredicto',
          color: CPD_COLORS.unknown,
          data: rows.map((r) => r.unknown)
        }
      ]
    };
  }, [rows, isDark, height]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

/** Barras horizontales de hangup causes (sirve para tiempo real y para histórico). */
export function HangupCausesChart({
  rows,
  height = 280
}: {
  rows: Array<{ key: string; count: number }>;
  height?: number;
}) {
  const isDark = useDarkMode();
  const options = useMemo<Highcharts.Options>(() => {
    const base = baseOptions(isDark);
    const sorted = [...rows].sort((a, b) => b.count - a.count);
    return {
      ...base,
      chart: { ...base.chart, height, type: 'bar' },
      xAxis: { ...base.xAxis, categories: sorted.map((r) => r.key || '—') },
      legend: { enabled: false },
      plotOptions: {
        series: { animation: false },
        bar: {
          borderWidth: 0,
          dataLabels: { enabled: true, style: { color: isDark ? '#cdcdde' : '#3f4254' } }
        }
      },
      series: [
        {
          type: 'bar',
          name: 'Llamadas',
          data: sorted.map((r) => ({
            y: r.count,
            color:
              r.key === 'NORMAL_CLEARING'
                ? CPD_COLORS.human
                : r.key === 'CALL_REJECTED'
                  ? CPD_COLORS.voicemail
                  : CPD_COLORS.unknown
          }))
        }
      ]
    };
  }, [rows, isDark, height]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export { fmtTime };
